import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ConfigProvider, App as AntApp, theme } from 'antd';
import { datadogRum } from '@datadog/browser-rum';

import env from 'env';

import App from './App';
import { antdLightTheme, antdDarkTheme } from 'styles/antd-theme';
import 'normalize.css';
import './styles/index.scss';

import { ErrorBoundary } from './error-boundary';
import reportWebVitals from './reportWebVitals';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 0,
    },
  },
});

process.env.NODE_ENV !== 'development' &&
  datadogRum.init({
    applicationId: env.DATADOG_APP_ID!,
    clientToken: env.DATADOG_CLIENT_TOKEN!,
    // `site` refers to the Datadog site parameter of your organization
    // see https://docs.datadoghq.com/getting_started/site/
    site: 'datadoghq.com',
    service: env.DATADOG_SERVICE!,
    env: env.DATADOG_ENV!,
    // Specify a version number to identify the deployed version of your application in Datadog
    version: env.DATADOG_BUILD_VERSION!,
    sessionSampleRate: Number(env.DATADOG_SESSION_SAMPLE_RATE!) || 100,
    sessionReplaySampleRate: Number(env.DATADOG_SESSION_REPLAY_SAMPLE_RATE!) || 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });

const Root = () => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(
    window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setIsDarkMode(e.matches);
      document.documentElement.classList.toggle('dark-theme', e.matches);
    };

    setIsDarkMode(mediaQuery.matches);
    document.documentElement.classList.toggle('dark-theme', mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider
          theme={{
            ...(isDarkMode ? antdDarkTheme : antdLightTheme),
            algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
          }}
        >
          <AntApp component={false}>
            <ErrorBoundary>
              <App />
            </ErrorBoundary>
          </AntApp>
        </ConfigProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
